import { ActivatedRouteSnapshot, DetachedRouteHandle, RouteReuseStrategy } from '@angular/router';

export interface ReviewOpsWorkspaceTab {
    id: string;
    title: string;
    url: string;
    scrollTop: number;
    createdAt: number;
}

export class ReviewOpsWorkspaceRouterState {
    private static activeTabId: string = '';
    private static detachingTabId: string = '';
    private static switchingTabs: boolean = false;
    private static routeHandles = new Map<string, DetachedRouteHandle>();
    private static routeParkedSlots = new Map<string, HTMLElement>();
    private static attachingKeys = new Set<string>();
    private static readonly parkingRootId: string = 'reviewops-workspace-route-parking';

    public static setActiveTab(tabId: string) {
        const nextTabId = tabId || '';
        if (this.activeTabId === nextTabId) return;
        this.activeTabId = nextTabId;
        this.dispatchWorkspaceActiveTabChanged(nextTabId);
    }

    public static beginTabNavigation(fromTabId: string, toTabId: string) {
        this.detachingTabId = fromTabId || this.activeTabId || '';
        this.activeTabId = toTabId || '';
        this.switchingTabs = !!this.detachingTabId && !!this.activeTabId && this.detachingTabId !== this.activeTabId;
    }

    public static finishTabNavigation() {
        this.detachingTabId = '';
        this.switchingTabs = false;
    }

    public static storeTabId() {
        return this.detachingTabId || this.activeTabId || '';
    }

    public static attachTabId() {
        return this.activeTabId || '';
    }

    public static isSwitchingTabs() {
        return this.switchingTabs;
    }

    public static getHandle(key: string) {
        return this.routeHandles.get(key) || null;
    }

    public static takeHandle(key: string) {
        const handle = this.routeHandles.get(key) || null;
        if (!handle) return null;
        this.routeHandles.delete(key);
        this.attachingKeys.add(key);
        this.notifyHandle(handle, 'reviewopsWorkspaceAttached');
        this.scheduleParkedSlotCleanup(key, handle);
        return handle;
    }

    public static hasHandle(key: string) {
        return this.routeHandles.has(key);
    }

    public static setHandle(key: string, handle: DetachedRouteHandle | null) {
        const oldHandle = this.routeHandles.get(key);
        if (oldHandle && oldHandle !== handle) {
            this.removeParkedSlot(key);
            this.destroyHandle(oldHandle);
        }
        if (!handle) {
            this.routeHandles.delete(key);
            if (!this.attachingKeys.has(key)) this.removeParkedSlot(key);
            return;
        }
        this.notifyHandle(handle, 'reviewopsWorkspaceDetached');
        this.routeHandles.set(key, handle);
        this.parkHandle(key, handle);
    }

    public static dropTab(tabId: string) {
        if (!tabId) return;
        this.clearTabHandles(tabId);
        this.dispatchWorkspaceTabDropped(tabId);
    }

    public static refreshTab(tabId: string) {
        if (!tabId) return;
        this.clearTabHandles(tabId);
        this.dispatchWorkspaceTabRefresh(tabId);
    }

    private static clearTabHandles(tabId: string) {
        const prefix = `${tabId}::`;
        Array.from(this.routeHandles.keys()).forEach((key: string) => {
            if (key.indexOf(prefix) !== 0) return;
            const handle = this.routeHandles.get(key);
            this.removeParkedSlot(key);
            if (handle) this.destroyHandle(handle);
            this.routeHandles.delete(key);
        });
    }

    private static destroyHandle(handle: DetachedRouteHandle) {
        const componentRef = this.handleComponentRef(handle);
        if (componentRef && typeof componentRef.destroy === 'function') {
            componentRef.destroy();
        }
    }

    private static notifyHandle(handle: DetachedRouteHandle | null, methodName: string) {
        const componentRef = this.handleComponentRef(handle);
        const instance = componentRef && componentRef.instance ? componentRef.instance : null;
        const method = instance && instance[methodName];
        if (typeof method !== 'function') return;
        try {
            method.call(instance);
        } catch (e) { }
    }

    private static handleComponentRef(handle: DetachedRouteHandle | null) {
        if (!handle) return null;
        return (handle as any)?.componentRef || handle;
    }

    private static handleHostElement(handle: DetachedRouteHandle | null) {
        const componentRef = this.handleComponentRef(handle);
        const nativeElement = componentRef && componentRef.location ? componentRef.location.nativeElement : null;
        if (nativeElement && nativeElement.nodeType === 1) return nativeElement as HTMLElement;
        const rootNodes = componentRef && componentRef.hostView ? componentRef.hostView.rootNodes || [] : [];
        for (let i = 0; i < rootNodes.length; i++) {
            const node = rootNodes[i];
            if (node && node.nodeType === 1) return node as HTMLElement;
        }
        return null;
    }

    private static parkHandle(key: string, handle: DetachedRouteHandle) {
        if (typeof document === 'undefined') return;
        const host = this.handleHostElement(handle);
        if (!host) return;
        const root = this.parkingRoot();
        if (!root) return;
        let slot = this.routeParkedSlots.get(key);
        if (!slot) {
            slot = document.createElement('div');
            slot.className = 'reviewops-workspace-route-parked-slot';
            slot.setAttribute('aria-hidden', 'true');
            slot.dataset.workspaceRouteCacheKey = key;
            this.applyParkedSlotStyle(slot);
            this.routeParkedSlots.set(key, slot);
        }
        root.appendChild(slot);
        slot.appendChild(host);
    }

    private static parkingRoot() {
        if (typeof document === 'undefined') return null;
        let root = document.getElementById(this.parkingRootId) as HTMLElement | null;
        if (root) return root;
        root = document.createElement('div');
        root.id = this.parkingRootId;
        root.setAttribute('aria-hidden', 'true');
        root.style.position = 'fixed';
        root.style.inset = '0';
        root.style.width = '0';
        root.style.height = '0';
        root.style.overflow = 'visible';
        root.style.pointerEvents = 'none';
        root.style.zIndex = '-1';
        document.body.appendChild(root);
        return root;
    }

    private static applyParkedSlotStyle(slot: HTMLElement) {
        slot.style.position = 'fixed';
        slot.style.inset = '0';
        slot.style.width = '100vw';
        slot.style.height = 'var(--reviewops-viewport-height, 100vh)';
        slot.style.overflow = 'hidden';
        slot.style.visibility = 'hidden';
        slot.style.opacity = '0';
        slot.style.pointerEvents = 'none';
        slot.style.zIndex = '-1';
    }

    private static scheduleParkedSlotCleanup(key: string, handle: DetachedRouteHandle) {
        const cleanup = () => {
            const slot = this.routeParkedSlots.get(key);
            const host = this.handleHostElement(handle);
            if (slot && (!host || host.parentElement !== slot)) this.removeParkedSlot(key);
            this.attachingKeys.delete(key);
        };
        if (typeof window === 'undefined') {
            cleanup();
            return;
        }
        window.setTimeout(() => {
            if (typeof window.requestAnimationFrame === 'function') {
                window.requestAnimationFrame(cleanup);
                return;
            }
            cleanup();
        }, 0);
    }

    private static removeParkedSlot(key: string) {
        const slot = this.routeParkedSlots.get(key);
        if (slot && slot.parentElement) slot.parentElement.removeChild(slot);
        this.routeParkedSlots.delete(key);
        this.attachingKeys.delete(key);
    }

    private static dispatchWorkspaceTabDropped(tabId: string) {
        if (typeof window === 'undefined' || typeof CustomEvent === 'undefined') return;
        try {
            window.dispatchEvent(new CustomEvent('reviewops-workspace-tab-dropped', { detail: { tabId } }));
        } catch (e) { }
    }

    private static dispatchWorkspaceTabRefresh(tabId: string) {
        if (typeof window === 'undefined' || typeof CustomEvent === 'undefined') return;
        try {
            window.dispatchEvent(new CustomEvent('reviewops-workspace-tab-refresh', { detail: { tabId } }));
        } catch (e) { }
    }

    private static dispatchWorkspaceActiveTabChanged(tabId: string) {
        if (typeof window === 'undefined' || typeof CustomEvent === 'undefined') return;
        try {
            window.dispatchEvent(new CustomEvent('reviewops-workspace-active-tab-changed', { detail: { tabId } }));
        } catch (e) { }
    }
}

export class ReviewOpsWorkspaceRouteReuseStrategy implements RouteReuseStrategy {
    public shouldDetach(route: ActivatedRouteSnapshot): boolean {
        return this.isCacheableRoute(route) && !!ReviewOpsWorkspaceRouterState.storeTabId();
    }

    public store(route: ActivatedRouteSnapshot, handle: DetachedRouteHandle | null): void {
        const tabId = ReviewOpsWorkspaceRouterState.storeTabId();
        const key = this.routeCacheKey(route, tabId);
        if (!key) return;
        ReviewOpsWorkspaceRouterState.setHandle(key, handle);
    }

    public shouldAttach(route: ActivatedRouteSnapshot): boolean {
        const key = this.routeCacheKey(route, ReviewOpsWorkspaceRouterState.attachTabId());
        return !!key && ReviewOpsWorkspaceRouterState.hasHandle(key);
    }

    public retrieve(route: ActivatedRouteSnapshot): DetachedRouteHandle | null {
        const key = this.routeCacheKey(route, ReviewOpsWorkspaceRouterState.attachTabId());
        if (!key) return null;
        return ReviewOpsWorkspaceRouterState.takeHandle(key);
    }

    public shouldReuseRoute(future: ActivatedRouteSnapshot, current: ActivatedRouteSnapshot): boolean {
        if (this.isWorkspaceKeepaliveRoute(future) && this.isWorkspaceKeepaliveRoute(current)) return true;
        if (future.routeConfig !== current.routeConfig) return false;
        if (ReviewOpsWorkspaceRouterState.isSwitchingTabs()) {
            return !this.isCacheableRoute(future);
        }
        return true;
    }

    private isCacheableRoute(route: ActivatedRouteSnapshot) {
        if (!route.routeConfig || !route.routeConfig.component || route.routeConfig.children) return false;
        if (this.isWorkspaceKeepaliveRoute(route)) return false;
        const path = this.routePath(route);
        return !!path && path !== '/login';
    }

    private isWorkspaceKeepaliveRoute(route: ActivatedRouteSnapshot) {
        return !!(route && route.data && route.data.workspaceKeepalive);
    }

    private routeCacheKey(route: ActivatedRouteSnapshot, tabId: string) {
        if (!tabId || !this.isCacheableRoute(route)) return '';
        const path = this.routePath(route);
        const query = this.routeQuery(route);
        return `${tabId}::${path}${query}`;
    }

    private routePath(route: ActivatedRouteSnapshot) {
        const segments: string[] = [];
        route.pathFromRoot.forEach((snapshot: ActivatedRouteSnapshot) => {
            (snapshot.url || []).forEach((segment: any) => {
                if (segment && segment.path) segments.push(segment.path);
            });
        });
        return `/${segments.join('/')}`.replace(/\/+$/, '') || '/';
    }

    private routeQuery(route: ActivatedRouteSnapshot) {
        const queryParams = route.queryParams || {};
        const keys = Object.keys(queryParams).sort();
        if (!keys.length) return '';
        const pairs: string[] = [];
        keys.forEach((key: string) => {
            const values = Array.isArray(queryParams[key]) ? queryParams[key] : [queryParams[key]];
            values.forEach((value: any) => {
                pairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(value == null ? '' : String(value))}`);
            });
        });
        return pairs.length ? `?${pairs.join('&')}` : '';
    }
}
